import React, { Component } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { getOutcomeOptions, getOutcomePrompt, getTaskTypeMeta } from '../../constants/taskTypeMeta';

/**
 * Required outcome picker, shown when completing a task type the server gates:
 * `inbound_call` / `consultation` (the outcome drives GoHighLevel re-engagement)
 * and `invoice_recovery` (the outcome is the audit record for why no invoice was
 * raised). Complete is rejected with a 400 without one.
 *
 * The options are per type, and an `allowedOutcomes` array from a server 400
 * overrides them — so a value added server-side still renders rather than
 * leaving the user with a picker whose every choice is rejected.
 *
 * Does NOT call the API itself — the parent owns the complete call and its
 * refetch/refresh logic, and passes errors back in via the `error` prop.
 */
class TaskOutcomeModal extends Component {
  state = {
    selectedOutcome: null,
    note: ''
  };

  componentDidUpdate(prevProps) {
    if (prevProps.task?.task_id !== this.props.task?.task_id) {
      this.setState({ selectedOutcome: null, note: '' });
    }
  }

  handleConfirm = () => {
    const { selectedOutcome, note } = this.state;
    if (!selectedOutcome) return;
    this.props.onConfirm({
      resolution_type: selectedOutcome,
      completion_note: note.trim() || null
    });
  };

  render() {
    const { show, task, onHide, submitting, error, allowedOutcomes } = this.props;
    const { selectedOutcome, note } = this.state;

    if (!task) return null;

    const meta = getTaskTypeMeta(task.task_type);
    const options = getOutcomeOptions(task.task_type, allowedOutcomes);

    return (
      <Modal show={show} onHide={submitting ? undefined : onHide} centered>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 600 }}>
            <i className={meta.icon} style={{ color: meta.accent, marginRight: 8 }} />
            {getOutcomePrompt(task.task_type)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
            {task.title}
          </div>

          {error && (
            <div className="alert alert-danger" style={{ fontSize: 13 }}>{error}</div>
          )}

          {options.map(opt => {
            const selected = selectedOutcome === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className="d-block w-100 text-left mb-2"
                onClick={() => this.setState({ selectedOutcome: opt.value })}
                disabled={submitting}
                style={{
                  border: selected ? `2px solid ${meta.accent}` : '1px solid var(--color-border)',
                  borderRadius: 8,
                  padding: '12px 16px',
                  // Tint from the type's accent — a hardcoded green wash read
                  // as an engagement task on the red invoice-recovery picker.
                  backgroundColor: selected ? `${meta.accent}0F` : 'var(--color-bg-card)',
                  cursor: 'pointer'
                }}
              >
                <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  <i
                    className={selected ? 'fa-sharp fa-solid fa-circle-check mr-2' : 'fa-sharp fa-regular fa-circle mr-2'}
                    style={{ color: selected ? meta.accent : 'var(--color-text-muted)' }}
                  />
                  {opt.label}
                </div>
                {opt.hint && (
                  <small className="text-muted d-block mt-1" style={{ marginLeft: 24 }}>
                    {opt.hint}
                  </small>
                )}
              </button>
            );
          })}

          <Form.Group className="mt-3 mb-0">
            <Form.Label style={{ fontSize: '0.85rem' }}>Note (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={note}
              onChange={(e) => this.setState({ note: e.target.value })}
              placeholder={task.task_type === 'invoice_recovery'
                ? "e.g. raised in QuickBooks as INV-1043"
                : "e.g. left voicemail"}
              disabled={submitting}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button
            style={{ background: meta.accent, borderColor: meta.accent }}
            onClick={this.handleConfirm}
            disabled={submitting || !selectedOutcome}
          >
            {submitting ? (
              <><i className="fa-sharp fa-solid fa-spinner fa-spin" /> Completing...</>
            ) : (
              'Complete Task'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default TaskOutcomeModal;
